import type { SharedRebateProgramPartnerIbStatus } from "../../api/generated";

import { EMPTY_DATE } from "../consts";

export type HoidPartner = {
  subIbId: string;
  name: string;
  kycStatus: string;
  splitPct: number;
  payout: number;
  ibStatus?: SharedRebateProgramPartnerIbStatus;
  /** The Master's undismissed rejection notification for this Sub IB; drives the dot. */
  rejectedNotificationId?: string | null;
};

export const hasUndismissedRejection = (partner: HoidPartner): boolean =>
  Boolean(partner.rejectedNotificationId?.trim());

export type Hoid = {
  id: string;
  rebateProgramLabel: string;
  status: "Active" | "Archived";
  rebateSplitPct: number;
  masterSplitPct: number;
  accountNumber: string;
  referralLink: string;
  lastPayment: string | null;
  clientAccounts: number;
  uniqueClients: number;
  tradingVolume: number;
  totalRebate: number;
  myPayout: number;
  subIbPayout: number;
  createdAt: string;
  partners: HoidPartner[];
  symbolOverrides?: SymbolOverride[];
  /**
   * The program's internal id (rebate_program.id) — distinct from `id`
   * (the displayed HOID/offer_id). Real-API-only: the detail/archive
   * endpoints key off this, not the HOID. Unset in the mock, where `id`
   * doubles as both.
   */
  programId?: string;
};

/**
 * Real-API-only performance figures scoped to the Program details drawer's
 * own date-range chip (see SharedRebateProgramsResources.programDetail) —
 * distinct from `Hoid`'s own fields, which are scoped to the page's date
 * filter. Deliberately excludes Clients/Client accounts: those are all-time
 * by design (see the drawer's own info tooltip) and always read from `Hoid`.
 */
export type ProgramDetailPatch = {
  totalRebate: number;
  myPayout: number;
  subIbPayout: number;
  tradingVolume: number;
  /**
   * Each Sub IB's own payout for this program within the drawer's date
   * range, keyed by `Hoid.partners[].subIbId` — a real join (both this and
   * the list endpoint's subIbId resolve to the same partner_ib_id), not a
   * positional guess.
   */
  subIbPayoutById: Record<string, number>;
};

/**
 * Load state for `ProgramDetailPatch`. `0` is a valid business value for any
 * of its fields, so it can never stand in for "not loaded yet" or "failed to
 * load" — this tracks that separately so the drawer never shows a fabricated
 * number.
 */
export type ProgramDetailPatchState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; value: ProgramDetailPatch };

export type SymbolOverride = {
  scopeType: "symbol" | "assetClass";
  scopeValue: string;
  masterSplitPct: number;
  partnerSplits: { subIbId: string; splitPct: number }[];
};

export type AssetGroup = {
  name: string;
  symbols: string[] | null;
};

export type SubIB = {
  id: string;
  name: string;
  email: string;
  kycStatus: "Verified" | "Pending";
  partnerId: string;
  comment: string;
  addedAt: string;
  hoids: Hoid[];
  uniqueClientsAgg: number;
  // Aggregated at build time. Phase 1 (EXC-01): KYC-pending enrolment is
  // out of scope — only KYC-approved partners ever join the roster (a
  // pending-KYC lookup blocks at the Create Program wizard's gate instead),
  // so this never needs a "Pending KYC" state.
  aggregateStatus: "Active" | "Inactive";
  rebateSplitMin: number;
  rebateSplitMax: number;
  lastPayment: string | null;
  clientAccounts: number;
  tradingVolume: number;
  totalRebate: number;
  myPayout: number;
  aggregateUniqueClients: number;
  aggregateSubIbPayout: number;
  sharedProgramCount: number;
};

// Kept for backward-compat with the programs view
export type Program = Hoid & { partners: HoidPartner[] };

const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

export const NEW_TAG_DAYS = 30;
export const isRecentlyAdded = (
  dateStr: string | null | undefined,
): boolean => {
  if (!dateStr) return false;
  const days = (Date.now() - new Date(dateStr).getTime()) / 86400000;
  return days <= NEW_TAG_DAYS && days >= -1;
};

function trkLink(id: string): string {
  let n = 0;
  for (const c of id) n = (n * 31 + c.charCodeAt(0)) >>> 0;
  return `trk.pepperstonepartners.com/aff_c?offer_id=${100 + (n % 900)}&aff_id=${10000 + ((n >>> 3) % 90000)}`;
}

function aggregate(hoids: Hoid[], uniqueClientsAgg: number) {
  const splits = hoids.map((h) => h.rebateSplitPct);
  let aggregateStatus: SubIB["aggregateStatus"] = "Inactive";
  if (hoids.some((h) => h.status === "Active")) aggregateStatus = "Active";
  return {
    aggregateStatus,
    rebateSplitMin: splits.length ? Math.min(...splits) : 0,
    rebateSplitMax: splits.length ? Math.max(...splits) : 0,
    lastPayment: hoids.reduce<string | null>(
      (acc, h) =>
        h.lastPayment && (!acc || h.lastPayment > acc) ? h.lastPayment : acc,
      null,
    ),
    clientAccounts: hoids.reduce((a, h) => a + h.clientAccounts, 0),
    tradingVolume: hoids.reduce((a, h) => a + h.tradingVolume, 0),
    totalRebate: hoids.reduce((a, h) => a + h.totalRebate, 0),
    myPayout: hoids.reduce((a, h) => a + h.myPayout, 0),
    aggregateUniqueClients: uniqueClientsAgg,
    aggregateSubIbPayout: hoids.reduce((a, h) => a + (h.subIbPayout ?? 0), 0),
  };
}

const RAW_SUBS: Omit<
  SubIB,
  | "aggregateStatus"
  | "rebateSplitMin"
  | "rebateSplitMax"
  | "lastPayment"
  | "clientAccounts"
  | "tradingVolume"
  | "totalRebate"
  | "myPayout"
  | "aggregateUniqueClients"
  | "aggregateSubIbPayout"
  | "sharedProgramCount"
>[] = [
  {
    id: "sub-1",
    name: "J**a S**h",
    email: "jessica.smith@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10421",
    comment: "Met at iFX Cyprus 2024. Strong producer in MENA.",
    addedAt: "2024-01-10",
    hoids: [
      {
        id: "9233",
        rebateProgramLabel: "HOID 9233",
        status: "Active",
        rebateSplitPct: 10,
        masterSplitPct: 90,
        accountNumber: "MT5-7401923",
        referralLink: trkLink("9233"),
        lastPayment: "2024-04-05",
        clientAccounts: 6,
        uniqueClients: 5,
        tradingVolume: 7450.67,
        totalRebate: 2450.67,
        myPayout: 2205.6,
        subIbPayout: 245.07,
        createdAt: "2024-01-15",
        partners: [],
      },
    ],
    uniqueClientsAgg: 5,
  },
  {
    id: "sub-2",
    name: "M**s C**n",
    email: "marcus.chen@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10488",
    comment: "",
    addedAt: "2024-05-20",
    hoids: [],
    uniqueClientsAgg: 0,
  },
  {
    id: "sub-4",
    name: "D**d O**r",
    email: "david.okafor@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10615",
    comment: "Splits leads across two FX channels; prefers monthly settlement.",
    addedAt: "2023-12-20",
    hoids: [
      {
        id: "9301",
        rebateProgramLabel: "HOID 9301",
        status: "Active",
        rebateSplitPct: 40,
        masterSplitPct: 60,
        accountNumber: "MT5-7403021",
        referralLink: trkLink("9301"),
        lastPayment: "2024-06-01",
        clientAccounts: 4,
        uniqueClients: 4,
        tradingVolume: 2400.5,
        totalRebate: 480.2,
        myPayout: 288.12,
        subIbPayout: 192.08,
        createdAt: "2024-03-08",
        partners: [],
      },
      {
        id: "9302",
        rebateProgramLabel: "HOID 9302",
        status: "Active",
        rebateSplitPct: 30,
        masterSplitPct: 70,
        accountNumber: "MT5-7403122",
        referralLink: trkLink("9302"),
        lastPayment: "2024-06-01",
        clientAccounts: 2,
        uniqueClients: 2,
        tradingVolume: 1200.25,
        totalRebate: 300.6,
        myPayout: 210.42,
        subIbPayout: 90.18,
        createdAt: "2024-05-19",
        partners: [],
      },
      {
        id: "9832",
        rebateProgramLabel: "HOID 9832",
        status: "Archived",
        rebateSplitPct: 20,
        masterSplitPct: 80,
        accountNumber: "MT5-7403905",
        referralLink: trkLink("9832"),
        lastPayment: "2024-05-22",
        clientAccounts: 0,
        uniqueClients: 0,
        tradingVolume: 0,
        totalRebate: 0,
        myPayout: 0,
        subIbPayout: 0,
        createdAt: "2024-01-04",
        partners: [],
      },
    ],
    uniqueClientsAgg: 5,
  },
  {
    id: "sub-5",
    name: "A**o T**a",
    email: "aiko.tanaka@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10712",
    comment: "",
    addedAt: "2024-07-15",
    hoids: [],
    uniqueClientsAgg: 0,
  },
  {
    id: "sub-6",
    name: "S**a R**s",
    email: "sofia.reyes@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10739",
    comment: "Archived since Q1 — keep historical reporting accessible.",
    addedAt: "2024-01-25",
    hoids: [
      {
        id: "9447",
        rebateProgramLabel: "HOID 9447",
        status: "Archived",
        rebateSplitPct: 15,
        masterSplitPct: 85,
        accountNumber: "MT5-7404475",
        referralLink: trkLink("9447"),
        lastPayment: "2024-02-28",
        clientAccounts: 3,
        uniqueClients: 3,
        tradingVolume: 940.1,
        totalRebate: 188.02,
        myPayout: 159.82,
        subIbPayout: 28.2,
        createdAt: "2024-02-01",
        partners: [],
      },
    ],
    uniqueClientsAgg: 3,
  },
  {
    id: "sub-7",
    name: "L**s B**r",
    email: "lukas.becker@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10802",
    comment: "Highest producer this quarter.",
    addedAt: "2024-03-15",
    hoids: [
      {
        id: "9501",
        rebateProgramLabel: "HOID 9501",
        status: "Active",
        rebateSplitPct: 25,
        masterSplitPct: 75,
        accountNumber: "MT5-7404921",
        referralLink: trkLink("9501"),
        lastPayment: "2024-08-14",
        clientAccounts: 12,
        uniqueClients: 11,
        tradingVolume: 14820.45,
        totalRebate: 3705.11,
        myPayout: 2778.83,
        subIbPayout: 926.28,
        createdAt: "2024-03-22",
        partners: [],
      },
      {
        id: "9502",
        rebateProgramLabel: "HOID 9502",
        status: "Active",
        rebateSplitPct: 30,
        masterSplitPct: 70,
        accountNumber: "MT5-7405011",
        referralLink: trkLink("9502"),
        lastPayment: null,
        clientAccounts: 0,
        uniqueClients: 0,
        tradingVolume: 0,
        totalRebate: 0,
        myPayout: 0,
        subIbPayout: 0,
        createdAt: daysAgo(4),
        partners: [],
      },
    ],
    uniqueClientsAgg: 11,
  },
  {
    id: "sub-8",
    name: "H**a P**k",
    email: "hana.park@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10855",
    comment: "",
    addedAt: "2024-07-28",
    hoids: [],
    uniqueClientsAgg: 0,
  },
  {
    id: "sub-9",
    name: "O**r H**d",
    email: "omar.haddad@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10901",
    comment: "Newest sub IB — recently onboarded.",
    hoids: [],
    uniqueClientsAgg: 0,
    addedAt: daysAgo(6),
  },
  {
    id: "sub-10",
    name: "E**a R**i",
    email: "elena.rossi@example.com",
    kycStatus: "Verified",
    partnerId: "PS-10942",
    comment: "",
    addedAt: "2024-06-30",
    hoids: [],
    uniqueClientsAgg: 0,
  },
  {
    id: "sub-11",
    name: "A**a S**h",
    email: "anaya.singh@example.com",
    kycStatus: "Verified",
    partnerId: "PS-11018",
    comment: "Runs two channels (educational + brokerage referrals).",
    addedAt: "2024-05-28",
    hoids: [
      {
        id: "9612",
        rebateProgramLabel: "HOID 9612",
        status: "Active",
        rebateSplitPct: 18,
        masterSplitPct: 82,
        accountNumber: "MT5-7406121",
        referralLink: trkLink("9612"),
        lastPayment: "2024-09-12",
        clientAccounts: 5,
        uniqueClients: 5,
        tradingVolume: 3050.2,
        totalRebate: 549.04,
        myPayout: 450.21,
        subIbPayout: 98.83,
        createdAt: "2024-06-01",
        partners: [],
      },
    ],
    uniqueClientsAgg: 5,
  },
];

// 2-sub shared programs
const SHARED_HOIDS: Hoid[] = [
  {
    id: "9613",
    rebateProgramLabel: "HOID 9613",
    status: "Active",
    masterSplitPct: 60,
    rebateSplitPct: 25,
    accountNumber: "MT5-7406134",
    referralLink: trkLink("9613"),
    lastPayment: "2024-09-12",
    clientAccounts: 3,
    uniqueClients: 2,
    tradingVolume: 1845.1,
    totalRebate: 405.92,
    myPayout: 243.55,
    subIbPayout: 101.48,
    createdAt: "2024-07-18",
    partners: [
      {
        subIbId: "sub-11",
        name: "A**a S**h",
        kycStatus: "Verified",
        splitPct: 25,
        payout: 101.48,
      },
      {
        subIbId: "sub-7",
        name: "L**s B**r",
        kycStatus: "Verified",
        splitPct: 15,
        payout: 60.89,
      },
    ],
  },
  {
    id: "9701",
    rebateProgramLabel: "HOID 9701",
    status: "Active",
    masterSplitPct: 60,
    rebateSplitPct: 25,
    accountNumber: "MT5-7407011",
    referralLink: trkLink("9701"),
    lastPayment: "2024-09-20",
    clientAccounts: 9,
    uniqueClients: 7,
    tradingVolume: 7360.4,
    totalRebate: 1840.5,
    myPayout: 1104.3,
    subIbPayout: 460.13,
    createdAt: "2024-08-02",
    symbolOverrides: [
      {
        scopeType: "symbol",
        scopeValue: "XAUUSD",
        masterSplitPct: 40,
        partnerSplits: [
          { subIbId: "sub-7", splitPct: 35 },
          { subIbId: "sub-8", splitPct: 25 },
        ],
      },
      {
        scopeType: "assetClass",
        scopeValue: "Commodities",
        masterSplitPct: 50,
        partnerSplits: [
          { subIbId: "sub-7", splitPct: 30 },
          { subIbId: "sub-8", splitPct: 20 },
        ],
      },
    ],
    partners: [
      {
        subIbId: "sub-7",
        name: "L**s B**r",
        kycStatus: "Verified",
        splitPct: 25,
        payout: 460.13,
      },
      {
        subIbId: "sub-8",
        name: "H**a P**k",
        kycStatus: "Verified",
        splitPct: 15,
        payout: 276.08,
      },
    ],
  },
  {
    id: "9714",
    rebateProgramLabel: "HOID 9714",
    status: "Active",
    masterSplitPct: 60,
    rebateSplitPct: 20,
    accountNumber: "MT5-7407144",
    referralLink: trkLink("9714"),
    lastPayment: "2024-09-28",
    clientAccounts: 5,
    uniqueClients: 4,
    tradingVolume: 4100.2,
    totalRebate: 920.4,
    myPayout: 552.24,
    subIbPayout: 184.08,
    createdAt: "2024-08-21",
    partners: [
      {
        subIbId: "sub-5",
        name: "A**o T**a",
        kycStatus: "Verified",
        splitPct: 20,
        payout: 184.08,
      },
      {
        subIbId: "sub-10",
        name: "E**a R**i",
        kycStatus: "Verified",
        splitPct: 20,
        payout: 184.08,
      },
    ],
  },
  {
    id: "9722",
    rebateProgramLabel: "HOID 9722",
    status: "Active",
    masterSplitPct: 60,
    rebateSplitPct: 25,
    accountNumber: "MT5-7407225",
    referralLink: trkLink("9722"),
    lastPayment: null,
    clientAccounts: 0,
    uniqueClients: 0,
    tradingVolume: 0,
    totalRebate: 0,
    myPayout: 0,
    subIbPayout: 0,
    createdAt: daysAgo(6),
    partners: [
      {
        subIbId: "sub-2",
        name: "M**s C**n",
        kycStatus: "Verified",
        splitPct: 25,
        payout: 0,
        ibStatus: "pendingKYC",
      },
      {
        subIbId: "sub-9",
        name: "O**r H**d",
        kycStatus: "Verified",
        splitPct: 15,
        payout: 0,
        ibStatus: "rejected",
        rejectedNotificationId: "mock-rejection-notification-sub-9",
      },
    ],
  },
];

// Build the final roster: inject shared programs, backfill partners, aggregate
const byId = new Map(RAW_SUBS.map((s) => [s.id, s]));
SHARED_HOIDS.forEach((h) => {
  h.partners.forEach((p) => {
    const sub = byId.get(p.subIbId);
    if (!sub) return;
    const perPartner: Hoid = {
      ...h,
      rebateSplitPct: p.splitPct,
      subIbPayout: p.payout,
    };
    sub.hoids.push(perPartner);
    sub.uniqueClientsAgg = (sub.uniqueClientsAgg ?? 0) + (h.uniqueClients ?? 0);
  });
});

// Backfill single-partner hoids
RAW_SUBS.forEach((s) => {
  s.hoids.forEach((h) => {
    if (!h.partners || h.partners.length === 0) {
      h.partners = [
        {
          subIbId: s.id,
          name: s.name,
          kycStatus: s.kycStatus,
          splitPct: h.rebateSplitPct,
          payout: h.subIbPayout,
        },
      ];
      h.masterSplitPct = 100 - h.rebateSplitPct;
    }
  });
});

export const SAMPLE_SUB_IBS: SubIB[] = RAW_SUBS.map((s) => {
  const agg = aggregate(s.hoids as Hoid[], s.uniqueClientsAgg);
  return {
    ...s,
    hoids: s.hoids as Hoid[],
    ...agg,
    sharedProgramCount: s.hoids.filter((h) => (h as Hoid).partners.length > 1)
      .length,
  };
});

export const programDisplayStatus = (p: Hoid): "Active" | "Archived" =>
  p.status === "Archived" ? "Archived" : "Active";

export const splitLabel = (hoid: Hoid, sep = " / "): string =>
  [hoid.masterSplitPct, ...hoid.partners.map((p) => p.splitPct)]
    .map((n) => `${n}%`)
    .join(sep);

/**
 * Per-share breakdown behind the Rebate split cell's tooltip — "You 70% ·
 * Alberto Torres 30%". You-first, matching `splitLabel`'s order and the
 * canvas's `splitTip` (`ProgramsView.tsx` → `ProgramRow`).
 */
export const splitBreakdown = (hoid: Hoid, youLabel = "You"): string =>
  [
    `${youLabel} ${hoid.masterSplitPct}%`,
    ...hoid.partners.map((p) => `${p.name} ${p.splitPct}%`),
  ].join(" · ");

export function collectPrograms(subs: SubIB[]): Hoid[] {
  const seen = new Set<string>();
  const out: Hoid[] = [];
  subs.forEach((s) =>
    s.hoids.forEach((h) => {
      if (seen.has(h.id)) return;
      seen.add(h.id);
      out.push(h);
    }),
  );
  return out;
}

// Keep old PROGRAMS export for the programs view (flat HOID list)
export const PROGRAMS = collectPrograms(SAMPLE_SUB_IBS);

export const fmtInt = (n: number) => n.toLocaleString("en-US");

// ── Create Program wizard: mock lookup ──────────────────────────────────────

export type LookupPartner = {
  partnerId: string;
  displayName: string;
  isFullNameShown: boolean;
  email: string;
  kycStatus: "Verified" | "Pending";
};

export type LookupExistingRelationship = {
  isExistingSubIb: boolean;
  /** Pre-fills the Relationship-settings step for an existing partner. */
  comment: string;
  aggregateStatus?: SubIB["aggregateStatus"];
  activeHoidCount: number;
  inactiveHoidCount: number;
  existingHoids: { hoidId: string; splitPct: number; status: string }[];
};

export type LookupResult =
  | {
      matched: false;
      error:
        | "invalid_email"
        | "self_lookup"
        | "server_error"
        | "not_found"
        | "no_rebate_account"
        | "rejected"
        | "rate_limited"
        /** The partner is already selected in the other lookup field. */
        | "duplicate";
      status?: number;
    }
  | {
      matched: false;
      error: "kyc_pending";
      /** Required: the message this error renders needs the Sub IB's name. */
      name: string;
      status?: number;
    }
  | {
      matched: true;
      partner: LookupPartner;
      existingRelationship: LookupExistingRelationship;
    };

/** Mock logged-in Master IB — also the address the OTP is "sent" to. */
export const LOGGED_IN_MASTER_EMAIL = "you@masterib.com";
// Phase 1 decision (NFR-02/FR-CP-08, PORT-2428 delta analysis): lock the
// gate after exactly 5 consecutive failed searches.
const INVALID_LOOKUP_LIMIT = 5;
// Module-level counter mirroring the Claude Design's abuse-guard: consecutive
// invalid/not-found lookups trip a rate limit until a match resets it.
let invalidLookupCount = 0;

/**
 * Records a counted failure and reports whether it trips the guard. Tripping
 * resets the counter — the caller serves a cooldown for it, so the window after
 * that starts clean. Without the reset the count stays above the limit and every
 * later failure re-trips immediately.
 */
function bumpInvalidLookupCount(): boolean {
  invalidLookupCount += 1;
  if (invalidLookupCount > INVALID_LOOKUP_LIMIT) {
    invalidLookupCount = 0;
    return true;
  }
  return false;
}

/** Test-only: resets the rate-limit counter between test cases. */
export function resetMockLookupRateLimit(): void {
  invalidLookupCount = 0;
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const maskWord = (s: string): string => {
  const chars = Array.from(s);
  const n = chars.length;
  if (n === 0) return "";
  if (n <= 2) return "*".repeat(n);
  if (n <= 4) return chars[0] + "*".repeat(n - 1);
  return chars[0] + "*".repeat(n - 2) + chars[n - 1];
};

/** Mock partner lookup for the Create Program wizard's email-lookup gate. */
export function mockLookupSubIb(email: string): Promise<LookupResult> {
  return new Promise((resolve) => {
    setTimeout(() => {
      const e = email.trim().toLowerCase();

      if (!e || !EMAIL_RE.test(e)) {
        resolve({
          matched: false,
          error: bumpInvalidLookupCount() ? "rate_limited" : "invalid_email",
        });
        return;
      }
      if (e === LOGGED_IN_MASTER_EMAIL || e === "self@masterib.com") {
        resolve({ matched: false, error: "self_lookup" });
        return;
      }
      if (e === "neterror@example.com") {
        resolve({ matched: false, error: "server_error" });
        return;
      }
      if (e === "norebate@example.com") {
        resolve({ matched: false, error: "no_rebate_account" });
        return;
      }
      if (e === "notfound@example.com" || /@notfound\./.test(e)) {
        resolve({
          matched: false,
          error: bumpInvalidLookupCount() ? "rate_limited" : "not_found",
        });
        return;
      }

      invalidLookupCount = 0;

      const existing = SAMPLE_SUB_IBS.find((s) => s.email.toLowerCase() === e);
      if (existing) {
        const activeHoidCount = existing.hoids.filter(
          (h) => h.status === "Active",
        ).length;
        resolve({
          matched: true,
          partner: {
            partnerId: existing.partnerId,
            displayName: existing.name,
            isFullNameShown: !existing.name.includes("*"),
            email: existing.email,
            kycStatus: "Verified",
          },
          existingRelationship: {
            isExistingSubIb: true,
            comment: existing.comment ?? "",
            aggregateStatus: existing.aggregateStatus,
            activeHoidCount,
            inactiveHoidCount: existing.hoids.length - activeHoidCount,
            existingHoids: existing.hoids.map((h) => ({
              hoidId: h.id,
              splitPct: h.rebateSplitPct,
              status: h.status,
            })),
          },
        });
        return;
      }

      if (e === "kycpending@example.com") {
        resolve({
          matched: true,
          partner: {
            partnerId: "PS-KYC01",
            displayName: "J**e D**e",
            isFullNameShown: false,
            email: e,
            kycStatus: "Pending",
          },
          existingRelationship: {
            isExistingSubIb: false,
            comment: "",
            activeHoidCount: 0,
            inactiveHoidCount: 0,
            existingHoids: [],
          },
        });
        return;
      }

      const local = e.split("@")[0] ?? "";
      const [firstRaw, lastRaw] = local.split(/[.\-_]/);
      const first = firstRaw || local || "New";
      const last = lastRaw || "Partner";
      const displayName = `${maskWord(first[0].toUpperCase() + first.slice(1))} ${maskWord(last[0].toUpperCase() + last.slice(1))}`;

      resolve({
        matched: true,
        partner: {
          partnerId: `PS-${Math.floor(10000 + Math.random() * 90000)}`,
          displayName,
          isFullNameShown: false,
          email: e,
          kycStatus: "Verified",
        },
        existingRelationship: {
          isExistingSubIb: false,
          comment: "",
          activeHoidCount: 0,
          inactiveHoidCount: 0,
          existingHoids: [],
        },
      });
    }, 600);
  });
}

// ── Create Program wizard: mock submit ──────────────────────────────────────

export type SubmitProgramResult = {
  hoidId: string;
  rebateProgramLabel: string;
  referralLink: string;
  status: "Active";
};

/**
 * Program-creation request as the wizard knows it. Deliberately expressed in the
 * UI's own vocabulary rather than the API body's: the portal adapter translates
 * (and splits `overrides` into the API's separate symbol/asset arrays), so this
 * package never imports a generated type.
 */
export type CreateProgramInput = {
  masterPct: number;
  partners: CreateProgramPartner[];
  /** Both scope types in one list; the adapter partitions on `scopeType`. */
  overrides: SymbolOverride[];
};

/**
 * One Sub IB's participation: their share plus the Privacy settings step's
 * answers. Both travel on the same entry because they describe the same
 * partner — a second list keyed by `ibId` could disagree with this one about
 * who is actually in the program.
 */
export type CreateProgramPartner = {
  ibId: string;
  pct: number;
  /**
   * The wizard's single "Show name and email" toggle. Stored server-side as two
   * separate permissions, so it is sent as two fields rather than one.
   */
  showName: boolean;
  showEmail: boolean;
  /** The Master's optional private note about this Sub IB. */
  comment: string;
};

/** Program-creation result, flattened from whatever the transport returned. */
export type CreateProgramOutput = {
  /** rebate_program.id — what the detail and archive endpoints key off. */
  programId: string;
  hoidId: string;
  rebateProgramLabel: string;
  referralLink: string;
  createdAt: string;
};

/**
 * Machine-readable failure classes the wizard can act on. Mirrors the `detail`
 * strings the create endpoint returns; `unknown` covers everything the UI has no
 * specific route for.
 */
export type CreateProgramErrorKind =
  | "program_cap_reached"
  | "no_rebate_account"
  | "sub_ib_not_found"
  | "invalid_request"
  | "dependency_error"
  | "unknown"
  | "step_up_required"
  | "step_up_failed"
  /**
   * The step-up redirect was abandoned and the page was restored from the
   * browser's bfcache (Back without completing the OTP). The pending create
   * resolves with this so the wizard simply returns to an idle Review step —
   * neither a success nor an error, just "nothing happened, try again".
   */
  | "step_up_cancelled";

export type CreateProgramRestorePartner = {
  partnerId: string;
  canonicalId: string;
  displayName: string;
  email: string;
  isExisting: boolean;
  identityToggle: boolean;
  comment: string;
  splitPct: number;
  kycStatus?: "Verified" | "Pending";
};

export type CreateProgramDisplay = {
  partners: CreateProgramRestorePartner[];
  overrides: SymbolOverride[];
};

export type CreateProgramApiOptions = {
  display?: CreateProgramDisplay;
  /** Aborted when the user leaves the redirect or "Creating your program…" screen (Back / Close). */
  signal?: AbortSignal;
  /** Called just before going to Auth0 for the OTP — only then is the redirect screen shown. */
  onRedirect?: () => void;
};

export type CreateProgramStepUp = {
  elevate: (signal: AbortSignal) => Promise<void>;
  retry: () => Promise<CreateProgramOutcome>;
};

export type PendingCreateResume = {
  /**
   * `verify`: an elevated token is cached (the OTP was completed) — reopen on
   * "Creating your program…" and replay the create. `review`: no elevated
   * token (the user came back without completing it) — reopen on Review.
   */
  mode: "verify" | "review";
  partners: CreateProgramDisplay["partners"];
  overrides: SymbolOverride[];
  /** Replays the create with the cached elevated token (verify mode); aborting `signal` cancels it. */
  run: (signal?: AbortSignal) => Promise<CreateProgramOutcome>;
  /** Drops the saved request (snapshot) once the Review step has been restored. */
  discard: () => void;
};

/**
 * Outcome union rather than a thrown error, matching `LookupResult`: a failed
 * create is an expected branch of this flow (cap reached, missing rebate
 * account), not an exception, and the wizard has to route several of them to
 * their own modals.
 */
export type CreateProgramOutcome =
  | { ok: true; program: CreateProgramOutput }
  | {
      ok: false;
      error: CreateProgramErrorKind;
      /** The rejected Sub IB id, when the API named one (404 sub_ib_not_found). */
      subIbId?: string;
    };

/** Mock program-creation submit. Rejects only for the documented error email. */
export function mockSubmitProgram(
  partnerEmail: string,
): Promise<SubmitProgramResult> {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (partnerEmail === "errorsubmit@example.com") {
        reject(new Error("submit_failed"));
        return;
      }
      const hoidId = String(9700 + Math.floor(Math.random() * 200));
      resolve({
        hoidId,
        rebateProgramLabel: `HOID ${hoidId}`,
        referralLink: trkLink(hoidId),
        status: "Active",
      });
    }, 800);
  });
}

// ── Session-created programs ────────────────────────────────────────────────

export type PartnerSpec = {
  canonicalId: string;
  isNew: boolean;
  name: string;
  email: string;
  kycStatus: "Verified" | "Pending";
  partnerId: string;
  comment?: string;
};

export type CreatedState = {
  subs: SubIB[];
  programs: Hoid[];
};

/** Ensures a proposed HOID id doesn't collide with the roster or session-created programs. */
export function freshHoidId(created: Hoid[], proposed: string): string {
  const taken = new Set<string>();
  SAMPLE_SUB_IBS.forEach((s) => s.hoids.forEach((h) => taken.add(h.id)));
  created.forEach((h) => taken.add(h.id));
  let id = proposed;
  while (taken.has(id)) id = String(Number(id) + 1);
  return id;
}

/**
 * Merges session-created Sub IBs/programs onto the base roster without
 * mutating SAMPLE_SUB_IBS — mirrors the Claude Design's clone-on-write
 * `baseData` derivation so Undo/re-render stay correct.
 */
export function buildRosterWithCreated(created: CreatedState): SubIB[] {
  if (created.subs.length === 0 && created.programs.length === 0) {
    return SAMPLE_SUB_IBS;
  }

  const roster: SubIB[] = [
    ...SAMPLE_SUB_IBS.map((s) => ({
      ...s,
      hoids: s.hoids.map((h) => ({ ...h })),
    })),
    ...created.subs.map((s) => ({
      ...s,
      hoids: s.hoids.map((h) => ({ ...h })),
    })),
  ];
  const byId = new Map(roster.map((s) => [s.id, s]));

  created.programs.forEach((h) => {
    h.partners.forEach((p) => {
      const sub = byId.get(p.subIbId);
      if (!sub) return;
      if (sub.hoids.some((x) => x.id === h.id)) return;
      sub.hoids.push({
        ...h,
        rebateSplitPct: p.splitPct,
        subIbPayout: p.payout,
      });
      sub.uniqueClientsAgg =
        (sub.uniqueClientsAgg ?? 0) + (h.uniqueClients ?? 0);
    });
  });

  roster.forEach((s) => {
    const agg = aggregate(s.hoids, s.uniqueClientsAgg);
    Object.assign(s, agg);
    s.sharedProgramCount = s.hoids.filter((h) => h.partners.length > 1).length;
  });

  return roster;
}

// Whole amounts show with no decimals ("9,000"); an amount with non-zero
// cents keeps exactly 2 decimals ("1,290,000.50") instead of losing a
// trailing zero to `toLocaleString`'s default of trimming to the minimum
// needed digits. Used by both the full (< 1 million) amount and the exact
// tooltip figure — never by the mln/bln branches below, which always force
// 2 decimals regardless of cents.
const fmtWithOptionalCents = (n: number): string =>
  n
    .toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/\.00$/, "");

// Below 1 million, trading volume is shown as an exact dollar figure (e.g.
// "9,000 USD"); at or above 1 million it collapses to the "mln USD"
// convention with 2 decimal places (e.g. "1.29 mln USD"); at or above 1
// billion it collapses further to "bln USD" (e.g. "1.29 bln USD") — without
// this a program earning at that scale would otherwise read as an
// unwieldy 4+-digit mln figure (e.g. "1,290.00 mln USD").
//
// Single source of truth for the three thresholds: `fmtVolMln` composes its
// combined string from this, and layouts that render the number and unit as
// two separately-styled elements (e.g. `StatCard`'s big-number + small-unit
// card) use the parts directly, so the two never drift out of sync.
export const fmtVolMlnParts = (n: number): { value: string; unit: string } => {
  const abs = Math.abs(n);
  if (abs < 1e6) {
    return { value: fmtWithOptionalCents(n), unit: "USD" };
  }
  if (abs < 1e9) {
    return {
      value: `${(n / 1e6).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} mln`,
      unit: "USD",
    };
  }
  return {
    value: `${(n / 1e9).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} bln`,
    unit: "USD",
  };
};

export const fmtVolMln = (n: number): string => {
  const { value, unit } = fmtVolMlnParts(n);
  return `${value} ${unit}`;
};

/** Full-precision counterpart to `fmtVolMln`, for the hover tooltip on an abbreviated ("mln"/"bln") figure. */
export const fmtVolExact = (n: number): string =>
  `${fmtWithOptionalCents(n)} USD`;

export const fmtDate = (d: string | null): string => {
  if (!d) return EMPTY_DATE;
  const date = new Date(d);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${String(date.getDate()).padStart(2, "0")} ${months[date.getMonth()]} ${date.getFullYear()}`;
};
